/**
 * Color generation utility for creating consistent and visually appealing color palettes
 */
export class ColorPaletteGenerator {
  // Base color collections
  static BASE_COLORS = {
    standard: [
      "#ff0000", // Bright Blue
      "#ff8700", // Green
      "#ffd300", // Soft Red
      "#deff0a", // Dark Gray-Blue
      "#a1ff0a", // Orange
      "#0aff99", // Purple
      "#0aefff", // Cyan
      "#147df5", // Muted Burgundy
      "#580aff", // Olive
      "#be0aff", // Deep Purple
    ],
    pastel: [
      "#AED9E0", // Soft Blue
      "#F3B562", // Light Orange
      "#F47C7C", // Soft Pink
      "#A0E7E5", // Mint
      "#B4F8C8", // Light Green
      "#FFAEBC", // Salmon Pink
      "#CBB3E5", // Lavender
      "#D3E0EA", // Light Steel Blue
      "#FBE7C6", // Pale Yellow
      "#E7DBFF", // Pale Lilac
    ],
    earth: [
      "#8C5F33", // Brown
      "#A0522D", // Sienna
      "#CD853F", // Peru
      "#DEB887", // Burlywood
      "#D2691E", // Chocolate
      "#5D4037", // Dark Brown
      "#6D4C41", // Darker Brown
      "#795548", // Brown Palette
      "#A1887F", // Rosy Brown
      "#BCAAA4", // Light Brown
    ],
    theme: [
      "#002A4B", // Primary
      "#f0b41c", // Secondary
      "#405F78", // Accent 1
      "#40464B", // Accent 2
      "#405F78", // Accent 3
      "#BFCAD2", // Accent 4
      "#6D4C41", // Accent 6
      "#37474F", // Neutral 1
      "#546E7A", // Neutral 2
    ],
  };

  /**
   * Generate a unique and distinct color palette
   * @param {number} count Number of colors to generate
   * @param {Object} [options] Configuration options
   * @param {string} [options.mode='standard'] Color mode
   * @param {boolean} [options.variation=true] Whether to add color variation
   * @returns {string[]} Array of distinct color hex strings
   */
  static generatePalette(count, options = {}) {
    const { mode = "standard", variation = true } = options;

    // Select base color collection
    const baseColors = this.BASE_COLORS[mode] || this.BASE_COLORS.standard;

    // Ensure we have enough colors
    const colors = [];
    let currentColors = [...baseColors];

    while (colors.length < count) {
      // If we've exhausted base colors, generate new ones
      if (currentColors.length === 0) {
        currentColors = [...baseColors];
      }

      // Select a base color
      const baseColorIndex = Math.floor(Math.random() * currentColors.length);
      const baseColor = currentColors.splice(baseColorIndex, 1)[0];

      // Apply variation if requested
      const finalColor = variation
        ? this.varyColor(baseColor, () => Math.random())
        : baseColor;

      colors.push(finalColor);
    }

    return colors.slice(0, count);
  }

  /**
   * Generate a color map with distinct colors
   * @param {string[]} names Array of names to generate colors for
   * @param {Object} [options] Color generation options
   * @returns {Object} Object mapping names to distinct color hex strings
   */
  static generateColorMap(names, options = {}) {
    const palette = this.generatePalette(names.length, options);

    return names.reduce((acc, name, index) => {
      acc[name] = palette[index];
      return acc;
    }, {});
  }

  /**
   * Create a more sophisticated color variation
   * @param {string} hexColor Base color to vary
   * @param {Function} randomFn Random number generator function
   * @returns {string} Varied color hex string
   */
  static varyColor(hexColor, randomFn) {
    // Convert hex to HSL for more meaningful color variation
    const { h, s, l } = this.hexToHSL(hexColor);

    // Vary hue, saturation, and lightness
    const hueShift = randomFn() * 60 - 30; // Shift hue by -30 to +30 degrees
    const satShift = randomFn() * 20 - 10; // Adjust saturation by -10% to +10%
    const lightShift = randomFn() * 10 - 5; // Adjust lightness by -5% to +5%

    // Create new HSL values
    const newH = (h + hueShift + 360) % 360;
    const newS = Math.max(0, Math.min(100, s + satShift));
    const newL = Math.max(0, Math.min(100, l + lightShift));

    // Convert back to hex
    return this.hslToHex(newH, newS, newL);
  }

  /** Convert HSL to Hex color
   * @param {number} h Hue (0-360)
   * @param {number} s Saturation (0-100)
   * @param {number} l Lightness (0-100)
   * @returns {string} Hex color string
   */
  static hslToHex(h, s, l) {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, "0");
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }
  /**
   * Convert Hex to HSL
   * @param {string} hex Hex color string
   * @returns {Object} HSL representation
   */
  static hexToHSL(hex) {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, "");

    // Parse hex to RGB
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h,
      s,
      l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return {
      h: h * 360,
      s: s * 100,
      l: l * 100,
    };
  }

  /**
   * Generate a deterministic color map that will be consistent across sessions
   * @param {string[]} names Array of names to generate colors for
   * @param {Object} [options] Color generation options
   * @returns {Object} Object mapping names to consistent color hex strings
   */
  static generateConsistentColorMap(names, options = {}) {
    const { mode = "standard" } = options;

    // Check if we should use the predefined color palette
    const useBasePalette = mode === "theme";
    const baseColors = this.BASE_COLORS[mode] || this.BASE_COLORS.standard;

    // Use a different approach depending on whether we use base palette or distribute hues
    const result = {};
    const nameHashes = {};

    // First, hash all names to get stable numbers for each
    names.forEach((name) => {
      nameHashes[name] = this.hashString(name);
    });

    if (useBasePalette) {
      // For theme mode, assign colors from the theme palette in a consistent way
      const assignedColors = new Set();

      // Process each name in original order
      names.forEach((name) => {
        // Skip if already assigned
        if (result[name]) return;

        // Determine which color to use from the base palette
        const hash = nameHashes[name];

        // Try to find a suitable color from the palette
        let selectedColor = null;

        // First try to assign a consistent color based on the name's hash
        const baseIndex = hash % baseColors.length;
        selectedColor = baseColors[baseIndex];

        // If this exact color is already used, we need to find an alternative
        if (assignedColors.has(selectedColor)) {
          // Try other colors in the palette
          let found = false;
          for (let i = 0; i < baseColors.length; i++) {
            const altColor = baseColors[(baseIndex + i) % baseColors.length];
            if (!assignedColors.has(altColor)) {
              selectedColor = altColor;
              found = true;
              break;
            }
          }

          // If all palette colors are used, create a variation of the base color
          if (!found) {
            const { h, s, l } = this.hexToHSL(selectedColor);

            // Apply a variation based on the hash
            const hueShift = 30 + (hash % 30);
            const newHue = (h + hueShift) % 360;

            // Ensure good saturation and lightness
            const newSat = Math.max(50, Math.min(90, s + (hash % 20) - 10));
            const newLight = Math.max(40, Math.min(60, l + (hash % 20) - 10));

            selectedColor = this.hslToHex(newHue, newSat, newLight);
          }
        }

        // Assign this color and mark it as used
        result[name] = selectedColor;
        assignedColors.add(selectedColor);
      });
    } else {
      // For non-theme mode, use the evenly distributed hue method
      const assignedHues = new Set();
      const nameToHue = {};

      // Pre-assign evenly distributed hues to each name based on their hash
      const distinctNames = [...new Set(names)]; // Remove duplicates for calculation

      distinctNames.forEach((name) => {
        const hash = nameHashes[name];

        // Use hash to generate a consistent index between 0-1
        const normalizedIndex = (hash % 10000) / 10000;

        // Convert to an evenly spaced hue (0-360)
        // Using golden ratio for optimal distribution
        const huePosition = (normalizedIndex + 0.618033988749895) % 1;
        const hue = Math.floor(huePosition * 360);

        nameToHue[name] = hue;
      });

      // Now process names in their original order
      names.forEach((name) => {
        // Skip if we already assigned a color to this name
        if (result[name]) return;

        let baseHue = nameToHue[name];
        let finalHue = baseHue;
        let attempts = 0;

        // Adjust hue if too close to an already assigned one
        while (this.isHueTooClose(finalHue, assignedHues) && attempts < 12) {
          finalHue = (baseHue + (attempts + 1) * 30) % 360;
          attempts++;
        }

        // For extreme cases, find the largest gap
        if (
          this.isHueTooClose(finalHue, assignedHues) &&
          assignedHues.size > 0
        ) {
          const sortedHues = Array.from(assignedHues).sort((a, b) => a - b);

          let maxGap = 0;
          let gapStart = 0;

          // Find largest gap in the hue circle
          for (let i = 0; i < sortedHues.length; i++) {
            const currentHue = sortedHues[i];
            const nextHue = sortedHues[(i + 1) % sortedHues.length];

            // Calculate gap (handling the wrap around 360)
            let gap = nextHue - currentHue;
            if (gap < 0) gap += 360;

            if (gap > maxGap) {
              maxGap = gap;
              gapStart = currentHue;
            }
          }

          // Place in middle of largest gap
          finalHue = (gapStart + maxGap / 2) % 360;
        }

        // Add this hue to used hues
        assignedHues.add(finalHue);

        // Calculate saturation and lightness from hash
        const hash = nameHashes[name];
        const saturation = 65 + ((hash % 1000) % 25); // 65-90%
        const lightness = 45 + ((hash % 500) % 15); // 45-60%

        // Assign color to this name
        result[name] = this.hslToHex(finalHue, saturation, lightness);
      });
    }

    return result;
  }

  /**
   * Check if a hue is too close to any in a set of used hues
   * @param {number} hue Hue to check (0-360)
   * @param {Set} usedHues Set of already used hues
   * @returns {boolean} True if the hue is too close to an existing one
   */
  static isHueTooClose(hue, usedHues) {
    const minDistance = 30; // Minimum 30 degrees between hues

    for (const usedHue of usedHues) {
      const distance = Math.min(
        Math.abs(hue - usedHue),
        360 - Math.abs(hue - usedHue)
      );

      if (distance < minDistance) {
        return true;
      }
    }

    return false;
  }

  /**
   * Calculate color similarity using color distance in HSL space
   * @param {string} color1 First color in hex format
   * @param {string} color2 Second color in hex format
   * @returns {number} Similarity score (0-1, where 1 is identical)
   */
  static calculateColorSimilarity(color1, color2) {
    const hsl1 = this.hexToHSL(color1);
    const hsl2 = this.hexToHSL(color2);

    // Calculate distance in HSL color space
    const hueDiff =
      Math.min(Math.abs(hsl1.h - hsl2.h), 360 - Math.abs(hsl1.h - hsl2.h)) /
      180.0;
    const satDiff = Math.abs(hsl1.s - hsl2.s) / 100.0;
    const lightDiff = Math.abs(hsl1.l - hsl2.l) / 100.0;

    // Weight the differences (hue is most important for differentiation)
    const weightedDiff = hueDiff * 0.6 + satDiff * 0.2 + lightDiff * 0.2;

    // Return similarity (inverse of difference)
    return 1 - weightedDiff;
  }

  /**
   * Simple string hashing function for deterministic color selection
   * @param {string} str String to hash
   * @returns {number} Deterministic hash value
   */
  static hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Create a deterministic color variation based on the input string
   * @param {string} hexColor Base color to vary
   * @param {string} inputString String to use for deterministic variation
   * @returns {string} Varied color hex string
   */
  static deterministicColorVariation(hexColor, inputString) {
    // Create deterministic values from the string
    const hash = this.hashString(inputString);

    // Use the hash to create pseudorandom but deterministic values
    const hueShiftSeed = (hash % 100) / 100;
    const satShiftSeed = ((hash >> 8) % 100) / 100;
    const lightShiftSeed = ((hash >> 16) % 100) / 100;

    // Convert hex to HSL for more meaningful color variation
    const { h, s, l } = this.hexToHSL(hexColor);

    // Vary hue, saturation, and lightness deterministically
    const hueShift = hueShiftSeed * 60 - 30; // Shift hue by -30 to +30 degrees
    const satShift = satShiftSeed * 20 - 10; // Adjust saturation by -10% to +10%
    const lightShift = lightShiftSeed * 10 - 5; // Adjust lightness by -5% to +5%

    // Create new HSL values
    const newH = (h + hueShift + 360) % 360;
    const newS = Math.max(0, Math.min(100, s + satShift));
    const newL = Math.max(0, Math.min(100, l + lightShift));

    // Convert back to hex
    return this.hslToHex(newH, newS, newL);
  }
}

// Example usage:
// const dealColors = ColorPaletteGenerator.generateColorMap(['Deal1', 'Deal2', 'Deal3']);
// const operatorColors = ColorPaletteGenerator.generateColorMap(['Operator1', 'Operator2'], { mode: 'pastel' });
// For consistent colors:
// const dealColors = ColorPaletteGenerator.generateConsistentColorMap(['Deal1', 'Deal2', 'Deal3']);
